import { useEffect, useMemo, useState } from "react";
import {
  Table,
  TableColumn,
  TableRowKey,
  Button,
  ButtonGroup,
} from "@datapay-by-datacom/design-system";
import { UseQueryResult } from "@tanstack/react-query";
import { WorkPatternRow } from "../../../generated/core-api";
import { WorkflowActionBar } from "../../../shared";
import {
  WorkPatternImportResult,
  WorkPatternImportParseFailedRow,
} from "../../hooks/WorkPatternImport/useWorkPatternImportInvocation";
import {
  clientParseErrorsByPatternIndex,
  clusterRows,
  countTabs,
  formatBlockedImportBanner,
  WORK_PATTERN_CONFIRM_IMPORT_BANNER_ID,
} from "./workPatternImportClustering";
import { mergeClientAndServerRowErrors } from "./workPatternImportErrorMerge";
import styles from "./WorkPatternImportTable.module.css";

type RowStatus = "pending" | "success" | "warning" | "error";

type StatusFilter = "all" | "failed" | "passed" | "warnings";

const STATUS_FILTERS: StatusFilter[] = ["all", "failed", "passed", "warnings"];

const EMPTY_IGNORED_INDICES = new Set<number>();

type WorkPatternTableRow = WorkPatternRow & {
  __index: number;
  __csvRowNumber: number;
  __status: RowStatus;
  __errorMessage?: string;
  __warningMessages?: string[];
};

interface WorkPatternImportTableProps {
  patterns: WorkPatternRow[];
  /**
   * The 200 envelope from a validate-only or commit call.
   * When present, rows show per-row status instead of Remove buttons.
   */
  result?: WorkPatternImportResult | null;
  /** Which phase produced the current result — drives header text and available actions. */
  resultPhase?: "validation" | "commit";
  /**
   * Per-row StandardHours/StandardDays mismatch warnings from the CSV parser,
   * keyed by pattern index. Shown in the Status column alongside pass/fail.
   */
  warnings?: Map<number, string[]>;
  /** Indices whose warnings the user dismissed (R14). */
  ignoredWarningIndices?: ReadonlySet<number>;
  /** When true, Confirm import is hidden until the user re-validates. */
  validationStale?: boolean;
  /** Parse-failed CSV rows merged into the validation report (mixed report). */
  parseFailedRows?: WorkPatternImportParseFailedRow[];
  /** CSV row numbers aligned with `patterns` (row 2 = first data row). */
  patternCsvRowNumbers?: number[];
  /** Row-level client parse errors (e.g. duplicate name on a row that still parsed). */
  parseErrors?: string[];
  onRemovePattern: (index: number) => void;
  onClearAll: () => void;
  onValidate: () => void;
  /** Opens the commit confirmation modal in the parent (#401). */
  onRequestCommitConfirm?: () => void;
  /** Shown beside Confirm import when the user tries to commit with blocking errors. */
  commitBlockedMessage?: string | null;
  /** Whether an issues CSV can be downloaded for the current result. */
  canDownloadErrors?: boolean;
  /** Downloads the current validation/commit issues as CSV. */
  onDownloadErrors?: () => void;
  onEditPattern?: (index: number) => void;
  onIgnoreWarning?: (index: number) => void;
  isProcessing?: boolean;
  /** When true, Cancel import is disabled so an in-flight commit cannot be abandoned from the UI. */
  isCommitting?: boolean;
  /**
   * Temporary gate for Edit in UI on blocking-error rows plus the new
   * edit-modal fields (#435). Default/undefined = today's behaviour (error
   * rows show no Edit; `!hasBlockingErrors` gate unchanged). Remove once GA —
   * see `FeatureFlag.EnableWorkPatternErrorEditInUi`.
   */
  enableErrorEditInUi?: boolean;
  /** Enables converged labels, downloads, partial import, and sticky actions. */
  redesignEnabled?: boolean;
}

const rowKey: TableRowKey<WorkPatternTableRow> = (row) =>
  `${row.workPatternName}-${row.__index}`;

/** Convert ISO YYYY-MM-DD to DD/MM/YYYY for display. Passes through unchanged if not ISO. */
function formatDate(iso: string): string {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
}

/** Optional CSV fields — match pay-component-import ReviewScreen empty-cell display. */
function formatOptionalCell(value: string | null | undefined): string {
  const trimmed = value?.trim();
  return trimmed ? trimmed : "—";
}

function hasActiveWarning(
  index: number,
  warnings: Map<number, string[]> | undefined,
  ignored: ReadonlySet<number>
): boolean {
  const messages = warnings?.get(index);
  return (messages?.length ?? 0) > 0 && !ignored.has(index);
}

function deriveRowStatus(
  index: number,
  hasResult: boolean,
  errorByIndex: Map<number, string>,
  clientErrorByIndex: Map<number, string[]>,
  warnings: Map<number, string[]> | undefined,
  ignored: ReadonlySet<number>
): RowStatus {
  if (!hasResult) {
    return "pending";
  }
  if (errorByIndex.has(index) || clientErrorByIndex.has(index)) {
    return "error";
  }
  if (hasActiveWarning(index, warnings, ignored)) {
    return "warning";
  }
  return "success";
}

function countValidationRowStatuses(
  patternCount: number,
  errorByIndex: Map<number, string>,
  clientErrorByIndex: Map<number, string[]>,
  warnings: Map<number, string[]> | undefined,
  ignored: ReadonlySet<number>
): { passed: number; warning: number } {
  let passed = 0;
  let warning = 0;
  for (let index = 0; index < patternCount; index++) {
    if (errorByIndex.has(index) || clientErrorByIndex.has(index)) {
      continue;
    }
    if (hasActiveWarning(index, warnings, ignored)) {
      warning++;
    } else {
      passed++;
    }
  }
  return { passed, warning };
}


function formatValidationReportHeader(
  passed: number,
  warningCount: number,
  total: number
): string {
  if (warningCount === 0) {
    return `Validation Report: ${passed} / ${total} passed`;
  }
  const warningLabel = warningCount === 1 ? "warning" : "warnings";
  return `Validation Report: ${passed} passed, ${warningCount} ${warningLabel} / ${total} total`;
}

function matchesStatusFilter(
  status: RowStatus,
  filter: StatusFilter
): boolean {
  if (filter === "all") {
    return true;
  }
  if (filter === "failed") {
    return status === "error";
  }
  if (filter === "passed") {
    return status === "success";
  }
  return status === "warning";
}

function formatStatusFilterLabel(
  filter: StatusFilter,
  counts: Record<StatusFilter, number>
): string {
  const labels: Record<StatusFilter, string> = {
    all: "All",
    failed: "Errors",
    passed: "Passed",
    warnings: "Warnings",
  };
  return `${labels[filter]} (${counts[filter]})`;
}

function countRowsByStatusFilter(
  rows: WorkPatternTableRow[]
): Record<StatusFilter, number> {
  const counts: Record<StatusFilter, number> = {
    all: rows.length,
    failed: 0,
    passed: 0,
    warnings: 0,
  };
  for (const row of rows) {
    if (row.__status === "error") {
      counts.failed++;
    } else if (row.__status === "success") {
      counts.passed++;
    } else if (row.__status === "warning") {
      counts.warnings++;
    }
  }
  return counts;
}

export function WorkPatternImportTable({
  patterns,
  result,
  resultPhase,
  warnings,
  ignoredWarningIndices,
  validationStale,
  onRemovePattern,
  onClearAll,
  onValidate,
  onRequestCommitConfirm,
  commitBlockedMessage,
  canDownloadErrors = false,
  onDownloadErrors,
  onEditPattern,
  onIgnoreWarning,
  isProcessing,
  isCommitting = false,
  parseFailedRows,
  patternCsvRowNumbers,
  parseErrors,
  enableErrorEditInUi = false,
  redesignEnabled = true,
}: WorkPatternImportTableProps) {
  const ignored = ignoredWarningIndices ?? EMPTY_IGNORED_INDICES;
  const hasResult = result !== null && result !== undefined;
  const isEmpty =
    patterns.length === 0 && (parseFailedRows?.length ?? 0) === 0;

  // Build an index → error map once so per-row lookups are O(1) instead of
  // O(rows × errors) via .find() on every render.
  const errorByIndex = useMemo(
    () => new Map((result?.errors ?? []).map((e) => [e.index, e.error])),
    [result?.errors]
  );

  const clientErrorByIndex = useMemo(
    () =>
      clientParseErrorsByPatternIndex(parseErrors, patternCsvRowNumbers),
    [parseErrors, patternCsvRowNumbers]
  );

  const validationTabCounts = useMemo(() => {
    if (!result || resultPhase === "commit") {
      return null;
    }
    return countTabs(
      clusterRows({
        patterns,
        result,
        warnings,
        ignoredWarningIndices: ignored,
        parseErrors,
        parseFailedRows,
        patternCsvRowNumbers,
      })
    );
  }, [
    result,
    resultPhase,
    patterns,
    warnings,
    ignored,
    parseErrors,
    parseFailedRows,
    patternCsvRowNumbers,
  ]);

  const allTableData = useMemo(() => {
    const tableData: WorkPatternTableRow[] = patterns.map((pattern, index) => {
      const serverError = errorByIndex.get(index);
      const clientMessages = clientErrorByIndex.get(index);
      const mergedError = mergeClientAndServerRowErrors(
        serverError,
        clientMessages
      );
      return {
        ...pattern,
        __index: index,
        __csvRowNumber: patternCsvRowNumbers?.[index] ?? index + 2,
        __status: deriveRowStatus(
          index,
          hasResult,
          errorByIndex,
          clientErrorByIndex,
          warnings,
          ignored
        ),
        __errorMessage: mergedError,
        __warningMessages: warnings?.get(index),
      };
    });

    const parseFailedTableRows: WorkPatternTableRow[] = (
      parseFailedRows ?? []
    ).map((failed, offset) => ({
      workPatternName: failed.workPatternName ?? `Row ${failed.csvRowNumber}`,
      workPatternCode: "",
      cycleDays: 0,
      startDate: "",
      payPublicHolidays: "No",
      standardHours: 0,
      standardDays: 0,
      dayHours: [],
      __index: patterns.length + offset,
      __csvRowNumber: failed.csvRowNumber,
      __status: "error",
      __errorMessage: failed.error,
    }));

    return [...tableData, ...parseFailedTableRows];
  }, [
    patterns,
    parseFailedRows,
    patternCsvRowNumbers,
    hasResult,
    errorByIndex,
    clientErrorByIndex,
    warnings,
    ignored,
  ]);

  const batchKey = useMemo(
    () =>
      [
        patterns.map((pattern) => pattern.workPatternName).join("\0"),
        parseFailedRows?.length ?? 0,
      ].join("|"),
    [patterns, parseFailedRows]
  );

  const resultKey = useMemo(() => {
    if (!result) {
      return null;
    }
    return [
      resultPhase ?? "",
      result.total,
      result.succeeded,
      result.failed,
      result.errors.length,
    ].join("|");
  }, [result, resultPhase]);

  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");

  useEffect(() => {
    setStatusFilter("all");
  }, [batchKey, resultKey]);

  const showStatusColumn = hasResult;

  const statusFilterCounts = useMemo(
    () => countRowsByStatusFilter(allTableData),
    [allTableData]
  );

  const filteredTableData = useMemo(() => {
    if (!showStatusColumn || statusFilter === "all") {
      return allTableData;
    }
    return allTableData.filter((row) =>
      matchesStatusFilter(row.__status, statusFilter)
    );
  }, [allTableData, showStatusColumn, statusFilter]);

  const scopeCodeHeader = "Work Pattern Code";

  const hasBlockingErrors =
    hasResult &&
    resultPhase === "validation" &&
    (validationTabCounts?.errors ?? 0) > 0;
  const canOfferWarningActions =
    (enableErrorEditInUi || !hasBlockingErrors) &&
    resultPhase === "validation" &&
    onEditPattern !== undefined &&
    onIgnoreWarning !== undefined;
  const canOfferErrorEditInUi =
    enableErrorEditInUi &&
    resultPhase === "validation" &&
    onEditPattern !== undefined;
  const rowActionsDisabled =
    redesignEnabled && Boolean(isProcessing || isCommitting);

  const { passed: passedCount, warning: warningCount } = countValidationRowStatuses(
    patterns.length,
    errorByIndex,
    clientErrorByIndex,
    warnings,
    ignored
  );

  const pendingColumns: TableColumn<WorkPatternTableRow>[] = [
    {
      key: "workPatternName",
      header() {
        return "Pattern Name";
      },
      cell(row) {
        return row.workPatternName;
      },
    },
    {
      key: "scopeCode" as keyof WorkPatternTableRow,
      header() {
        return scopeCodeHeader;
      },
      cell(row) {
        return formatOptionalCell(row.workPatternCode);
      },
    },
    {
      key: "cycleDays",
      header() {
        return "Cycle Days";
      },
      cell(row) {
        return String(row.cycleDays);
      },
    },
    {
      key: "standardHours",
      header() {
        return "Std Hours";
      },
      cell(row) {
        return String(row.standardHours);
      },
    },
    {
      key: "startDate",
      header() {
        return "Start Date";
      },
      cell(row) {
        return formatDate(row.startDate);
      },
    },
    {
      key: "actions" as keyof WorkPatternTableRow,
      header() {
        return "Actions";
      },
      cell(row) {
        return (
          <Button
            variant="tertiary"
            onPress={() => onRemovePattern(row.__index)}
          >
            Remove
          </Button>
        );
      },
    },
  ];

  const resultColumns: TableColumn<WorkPatternTableRow>[] = [
    {
      key: "csvRowNumber" as keyof WorkPatternTableRow,
      header() {
        return "Row";
      },
      cell(row) {
        return String(row.__csvRowNumber);
      },
    },
    {
      key: "workPatternName",
      header() {
        return "Pattern Name";
      },
      cell(row) {
        return row.workPatternName;
      },
    },
    {
      key: "scopeCode" as keyof WorkPatternTableRow,
      header() {
        return scopeCodeHeader;
      },
      cell(row) {
        return formatOptionalCell(row.workPatternCode);
      },
    },
    {
      key: "cycleDays",
      header() {
        return "Cycle Days";
      },
      cell(row) {
        return String(row.cycleDays);
      },
    },
    {
      key: "standardHours",
      header() {
        return "Std Hours";
      },
      cell(row) {
        return String(row.standardHours);
      },
    },
    {
      key: "startDate",
      header() {
        return "Start Date";
      },
      cell(row) {
        return formatDate(row.startDate);
      },
    },
    {
      key: "status" as keyof WorkPatternTableRow,
      header() {
        return "Status";
      },
      cell(row) {
        if (row.__status === "success") {
          return (
            <div className={styles.checkInner}>
              <span className={styles.badgePass} aria-label="Passed">
                Pass
              </span>
            </div>
          );
        }
        if (row.__status === "warning") {
          const rowWarnings = row.__warningMessages ?? [];
          return (
            <div className={styles.checkInner}>
              <span className={styles.badgeWarning} aria-label="Warning">
                Warning
              </span>
              <ul className={styles.reasons}>
                {rowWarnings.map((message, i) => (
                  <li key={i} className={styles.statusWarning}>
                    {message}
                  </li>
                ))}
              </ul>
              {canOfferWarningActions && (
                <div className={styles.warningActions}>
                  <Button
                    variant="secondary"
                    onPress={() => onEditPattern!(row.__index)}
                    isDisabled={rowActionsDisabled}
                  >
                    Edit in UI
                  </Button>
                  <Button
                    variant="secondary"
                    onPress={() => onIgnoreWarning!(row.__index)}
                    isDisabled={rowActionsDisabled}
                  >
                    {redesignEnabled ? "Dismiss warning" : "Ignore"}
                  </Button>
                </div>
              )}
            </div>
          );
        }
        if (row.__status === "error") {
          return (
            <div className={styles.checkInner}>
              <span className={styles.badgeFail} aria-label="Error">
                Error
              </span>
              <ul className={styles.reasons}>
                <li className={styles.reason}>
                  {row.__errorMessage ?? "Unknown error"}
                </li>
              </ul>
              {canOfferErrorEditInUi && row.__index < patterns.length && (
                <div className={styles.warningActions}>
                  <Button
                    variant="secondary"
                    onPress={() => onEditPattern!(row.__index)}
                    isDisabled={rowActionsDisabled}
                  >
                    Edit in UI
                  </Button>
                </div>
              )}
            </div>
          );
        }
        return "—";
      },
    },
  ];

  const columns = showStatusColumn ? resultColumns : pendingColumns;
  const allSucceeded = hasResult && result.failed === 0;

  if (isEmpty) {
    return null;
  }

  return (
    <div className={styles.tableContainer}>
      <div className={styles.tableHeader}>
        <h3>
          {result && resultPhase === "commit"
            ? `Import Results: ${result.succeeded} / ${result.total} succeeded`
            : result && resultPhase === "validation"
              ? formatValidationReportHeader(
                  passedCount,
                  warningCount,
                  result.total
                )
              : `Pending Patterns (${patterns.length})`}
        </h3>

        {!redesignEnabled && !result && (
          <ButtonGroup>
            <Button
              variant="tertiary"
              onPress={onClearAll}
              isDisabled={isCommitting}
            >
              Clear All
            </Button>
            <Button
              variant="primary"
              onPress={onValidate}
              isDisabled={isProcessing}
            >
              {isProcessing ? "Processing..." : "Validate & Import"}
            </Button>
          </ButtonGroup>
        )}

        {!redesignEnabled &&
          result &&
          allSucceeded &&
          validationStale &&
          resultPhase !== "commit" && (
            <ButtonGroup>
              <Button
                variant="tertiary"
                onPress={onClearAll}
                isDisabled={isCommitting}
              >
                Cancel import
              </Button>
              <Button
                variant="primary"
                onPress={onValidate}
                isDisabled={isProcessing}
              >
                {isProcessing ? "Processing..." : "Re-validate"}
              </Button>
            </ButtonGroup>
          )}
      </div>

      {hasBlockingErrors && validationTabCounts && (
        <div className={styles.blockedBanner} role="alert">
          {formatBlockedImportBanner(
            validationTabCounts.errors,
            validationTabCounts.all,
            redesignEnabled
              ? validationTabCounts.passed + validationTabCounts.warnings
              : validationTabCounts.passed
          )}
        </div>
      )}

      {showStatusColumn && (
        <div
          className={styles.filterBar}
          role="group"
          aria-label="Filter validation report by status"
        >
          <ButtonGroup>
            {STATUS_FILTERS.map((filter) => (
              <Button
                key={filter}
                variant={statusFilter === filter ? "secondary" : "tertiary"}
                aria-pressed={statusFilter === filter}
                onPress={() => setStatusFilter(filter)}
              >
                {formatStatusFilterLabel(filter, statusFilterCounts)}
              </Button>
            ))}
          </ButtonGroup>
        </div>
      )}

      <div className={redesignEnabled ? styles.tableScroll : undefined}>
        <Table
          description="Work Pattern Import Preview"
          config={{
            columns,
            rowKey,
          }}
          control={{ params: {}, onParamsChange: () => {} }}
          query={
            {
              data: filteredTableData,
              isLoading: false,
              error: null,
            } as UseQueryResult<WorkPatternTableRow[]>
          }
        />
      </div>

      {redesignEnabled && !result && (
        <WorkflowActionBar
          ariaLabel="Work pattern import actions"
          leadingActions={
            <Button
              variant="secondary"
              onPress={onClearAll}
              isDisabled={isCommitting}
            >
              Cancel import
            </Button>
          }
          trailingActions={
            <Button
              variant="primary"
              onPress={onValidate}
              isDisabled={isProcessing}
            >
              {isProcessing ? "Processing..." : "Validate"}
            </Button>
          }
        />
      )}

      {redesignEnabled && result && resultPhase !== "commit" && (
        <WorkflowActionBar
          ariaLabel="Work pattern import actions"
          status={
            commitBlockedMessage && !validationStale ? (
            <div
              id={WORK_PATTERN_CONFIRM_IMPORT_BANNER_ID}
              className={styles.blockedBanner}
              role="alert"
            >
              {commitBlockedMessage}
            </div>
            ) : undefined
          }
          leadingActions={
            <Button
              variant="secondary"
              onPress={onClearAll}
              isDisabled={isCommitting}
            >
              Cancel import
            </Button>
          }
          trailingActions={
            <div className={styles.tableFooterActions}>
              {canDownloadErrors && onDownloadErrors && (
                <Button
                  variant="secondary"
                  onPress={onDownloadErrors}
                  isDisabled={isProcessing}
                >
                  Download issues CSV
                </Button>
              )}
              {validationStale ? (
                <Button
                  variant="primary"
                  onPress={onValidate}
                  isDisabled={isProcessing || isCommitting}
                >
                  {isProcessing ? "Processing..." : "Re-validate"}
                </Button>
              ) : (
                onRequestCommitConfirm && (
                  <Button
                    variant="primary"
                    onPress={onRequestCommitConfirm}
                    isDisabled={isProcessing || isCommitting}
                    aria-describedby={
                      commitBlockedMessage
                        ? WORK_PATTERN_CONFIRM_IMPORT_BANNER_ID
                        : undefined
                    }
                  >
                    {isCommitting ? "Importing..." : "Confirm import"}
                  </Button>
                )
              )}
            </div>
          }
        />
      )}

      {redesignEnabled && result && resultPhase === "commit" && (
        <WorkflowActionBar
          ariaLabel="Work pattern import actions"
          leadingActions={
            <Button variant="secondary" onPress={onClearAll}>
              Import new work patterns
            </Button>
          }
          trailingActions={
            canDownloadErrors && onDownloadErrors ? (
              <Button variant="secondary" onPress={onDownloadErrors}>
                Download issues CSV
              </Button>
            ) : undefined
          }
        />
      )}

      {!redesignEnabled &&
        result &&
        onRequestCommitConfirm &&
        resultPhase !== "commit" &&
        !validationStale && (
          <footer className={styles.legacyTableFooter}>
            {commitBlockedMessage && (
              <div
                id={WORK_PATTERN_CONFIRM_IMPORT_BANNER_ID}
                className={styles.blockedBanner}
                role="alert"
              >
                {commitBlockedMessage}
              </div>
            )}
            <div className={styles.legacyTableFooterActions}>
              <Button
                variant="tertiary"
                onPress={onClearAll}
                isDisabled={isCommitting}
              >
                Cancel import
              </Button>
              <Button
                variant="primary"
                onPress={onRequestCommitConfirm}
                isDisabled={isProcessing}
                aria-describedby={
                  commitBlockedMessage
                    ? WORK_PATTERN_CONFIRM_IMPORT_BANNER_ID
                    : undefined
                }
              >
                {isCommitting ? "Importing..." : "Confirm Import"}
              </Button>
            </div>
          </footer>
        )}

      {!redesignEnabled && result && resultPhase === "commit" && (
        <footer className={styles.legacyTableFooter}>
          <div className={styles.legacyTableFooterActions}>
            <Button variant="tertiary" onPress={onClearAll}>
              Import new work patterns
            </Button>
          </div>
        </footer>
      )}
    </div>
  );
}
